import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PlayerListViewModel, PlayerTagViewModel } from '../../../shared/models/player.model';
import { PlayerService } from '../../../shared/services/player.service';
import { PositionPipe } from '../../../shared/pipes/position.pipe';
import { forkJoin, of } from 'rxjs';
import { map, switchMap, finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface PlayerTagGroup {
  tagId: string;
  tagText: string;
  tagColor?: string;
  players: PlayerListViewModel[];
}

@Component({
  selector: 'app-players-by-tag',
  imports: [
    RouterLink,
    DatePipe,
    PositionPipe,
    MatButtonModule,
    MatToolbarModule,
    MatExpansionModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './players-by-tag.component.html',
  styleUrl: './players-by-tag.component.css'
})
export class PlayersByTagComponent implements OnInit {
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
  tagGroups: PlayerTagGroup[] = [];
  isLoading = false;
  loadError = false;
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  constructor(private playerService: PlayerService) {}

  ngOnInit(): void {
    this.loadPlayersByTag();
  }

  getPlayerId(player: PlayerListViewModel): string {
    return this.getFirstValidId([
      player.LifelineScoutingId,
      player.PlayerView?.LifelineScoutingId,
      player.Player?.LifelineScoutingId,
      player.PlayerId,
      player.PlayerView?.PlayerId
    ]);
  }

  getLastName(player: PlayerListViewModel): string {
    return player.PlayerView?.LastName ?? player.Player?.LastName ?? '';
  }

  getFirstName(player: PlayerListViewModel): string {
    return player.PlayerView?.FirstName ?? player.Player?.FirstName ?? '';
  }

  getPositionId(player: PlayerListViewModel): number {
    return player.PlayerView?.PositionId ?? player.Player?.PositionId ?? 0;
  }

  getDateOfBirth(player: PlayerListViewModel): Date | null {
    return player.PlayerView?.DateOfBirth ?? player.Player?.DateOfBirth ?? null;
  }

  private loadPlayersByTag(): void {
    this.isLoading = true;
    this.loadError = false;

    this.playerService.getActivePlayerTags().pipe(
      switchMap((tags) => {
        const activeTags = tags ?? [];

        if (activeTags.length === 0) {
          return of([] as PlayerTagGroup[]);
        }

        return forkJoin(
          activeTags.map((tag) =>
            this.playerService.getPlayersByTag(tag.TagId).pipe(
              map((players) => this.buildTagGroup(tag, players ?? []))
            )
          )
        );
      }),
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.isLoading = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (groups) => {
        this.tagGroups = groups.sort((a, b) => a.tagText.localeCompare(b.tagText));
        this.cdr.detectChanges();
      },
      error: () => {
        this.tagGroups = [];
        this.loadError = true;
        this.cdr.detectChanges();
      }
    });
  }

  private buildTagGroup(tag: PlayerTagViewModel, players: PlayerListViewModel[]): PlayerTagGroup {
    return {
      tagId: tag.TagId,
      tagText: tag.Text ?? tag.TagText,
      tagColor: tag.TagColor,
      players: [...players].sort((a, b) => {
        const left = `${this.getLastName(a)} ${this.getFirstName(a)}`.toLowerCase();
        const right = `${this.getLastName(b)} ${this.getFirstName(b)}`.toLowerCase();
        return left.localeCompare(right);
      })
    };
  }

  private getFirstValidId(ids: Array<string | undefined>): string {
    for (const id of ids) {
      if (id && id.trim() !== '' && id.toLowerCase() !== this.emptyGuid) {
        return id;
      }
    }

    return '';
  }
}
